import { useMemo } from 'react';
import { ApiError } from '@/api/client';
import { Dimensions, Metrics, type OverviewResponse } from '@/api/types';
import { metricValue, resolveMetric, sourceLabel } from '@/api/metric-resolution';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { LiveCard } from '@/components/LiveCard';
import { ExportButton } from '@/components/ExportButton';
import { RangeBar } from '@/components/RangeBar';
import { ResolvedTile } from '@/components/ResolvedTile';
import { StatTile } from '@/components/StatTile';
import { TrendChart, type TrendSeries } from '@/components/TrendChart';
import { breakdown, toBars, useOverview } from '@/hooks/useOverview';

export function OverviewPage({ siteKey }: { siteKey: string }) {
  const { data, isLoading, isError, error, days, setDays, from, to, setRange, isCustomRange } =
    useOverview(siteKey);

  return (
    <>
      {/* Live first — "is the site up right now" is the question people open a dashboard with.
          Independent query, so a slow or failed overview never blocks it. */}
      <LiveCard siteKey={siteKey} />

      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        // No dimension: the overview exports the WHOLE workbook — summary, the dated daily sheet,
        // and every breakdown. The per-dimension pages export just their own table.
        actions={<ExportButton siteKey={siteKey} from={from} to={to} label="Export all (Excel)" />}
      />

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}

      {isError && (
        <Callout tone="error" title="Could not load analytics">
          {error instanceof ApiError && error.status === 404
            ? `No analytics sources are connected for "${siteKey}". Add one on the Sources page.`
            : error instanceof ApiError
              ? error.message
              : 'Unexpected error.'}
        </Callout>
      )}

      {data && <OverviewBody data={data} />}
    </>
  );
}

function OverviewBody({ data }: { data: OverviewResponse }) {
  const totals = data.totals;

  /**
   * ONE sessions line, resolved GA4-first — not one line per provider.
   *
   * Plotting GA4 and Clarity side by side put two different numbers on screen both labelled
   * "sessions", inviting the reader to treat the gap as signal. It is not: Clarity counts bot
   * sessions GA4 filters, and ad-blockers suppress the two unequally, so the divergence measures
   * instrumentation rather than traffic. Under GA4-primary there is one answer, and the subtitle
   * names who gave it.
   */
  const { series, sessionSource } = useMemo(() => {
    const points = data.series.map((p) => ({
      date: p.date,
      resolved: resolveMetric(p.metrics, 'sessions'),
    }));

    if (points.every((p) => p.resolved === null)) {
      return { series: [] as TrendSeries[], sessionSource: undefined };
    }

    // One line per provider that answered, each a gap on the days it did not: a day Clarity stood in for GA4 is drawn
    // as Clarity's, never joined into GA4's line, and a day nobody measured is a gap — never a drop to 0.
    const sources = [...new Set(points.flatMap((p) => (p.resolved ? [p.resolved.source] : [])))];
    const colors = ['var(--series-1)', 'var(--series-2)'];
    const days = (source: string) => points.filter((p) => p.resolved?.source === source).length;

    return {
      series: sources.slice(0, colors.length).map((source, i) => ({
        key: `sessions-${source}`,
        label: `Sessions (${sourceLabel(points.find((p) => p.resolved?.source === source)!.resolved) ?? source})`,
        color: colors[i]!,
        points: points.map((p) => ({ date: p.date, value: p.resolved?.source === source ? p.resolved.value : null })),
      })) as TrendSeries[],
      sessionSource: sources
        .map((source) => `${sourceLabel(points.find((p) => p.resolved?.source === source)!.resolved) ?? source} ${days(source)} day${days(source) === 1 ? '' : 's'}`)
        .join(' · '),
    };
  }, [data.series]);

  return (
    <>
      {/* Every tile resolves through MetricChains, so GA4 wins wherever it measures the thing.
          The `sub` states WHICH provider answered — when Clarity has to stand in for GA4 the
          number means something slightly different (Clarity counts bots GA4 filters), and a tile
          that hides that is quietly lying about its own figure. */}
      <div className="kpi-row">
        <ResolvedTile label="Sessions" totals={totals} chain="sessions" />
        <ResolvedTile label="Users" totals={totals} chain="users" />
        <ResolvedTile label="Page views" totals={totals} chain="pageViews" />
        {/* Conversions. Leads come from your own database, so this is the only number here
            that is a fact rather than an estimate — ad-blockers suppress GA4 and Clarity,
            but they cannot suppress a row in your own Mongo. */}
        <StatTile label="Leads" value={totals[Metrics.leads] ?? null} sub="First-party" />
        <ConversionRateTile totals={totals} />
        {/* Clarity-only by nature: GA4 has no concept of a rage click. */}
        <ResolvedTile label="Rage clicks" totals={totals} chain="rageClicks" tone="critical" />
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Sessions over time</h2>
          {sessionSource && <span className="t-label subtle">{sessionSource}</span>}
        </div>
        <div className="card-body">
          <TrendChart series={series} missingDates={data.missing_dates} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
        <MiniCard title="Top countries" rows={toBars(breakdown(data, Dimensions.country))} />
        <MiniCard title="Top pages" rows={toBars(breakdown(data, Dimensions.pagePath))} />
        <MiniCard title="Top sources" rows={toBars(breakdown(data, Dimensions.source))} />
      </div>
    </>
  );
}

/**
 * Leads ÷ sessions. Renders "—" rather than 0% when either side is missing, because a
 * conversion rate of zero and "we have no conversion data" mean completely different things
 * and confusing them would make a working funnel look broken.
 */
function ConversionRateTile({ totals }: { totals: Record<string, number> }) {
  const leads = totals[Metrics.leads];
  // GA4-first via the shared policy — a conversion rate computed against Clarity's session
  // count (which includes bots) would understate the real rate.
  const sessions = metricValue(totals, 'sessions') ?? undefined;

  if (leads === undefined || !sessions) {
    return <StatTile label="Conversion rate" value={null} sub="needs leads + sessions" />;
  }

  const pct = (leads / sessions) * 100;

  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">Conversion rate</div>
      <div className="stat-value">{pct < 1 ? pct.toFixed(2) : pct.toFixed(1)}%</div>
      <div className="stat-sub t-caption">
        {leads.toLocaleString('en-US')} leads / {sessions.toLocaleString('en-US')} sessions
      </div>
    </div>
  );
}

function MiniCard({ title, rows }: { title: string; rows: { label: string; value: number }[] }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
      </div>
      <div className="card-body">
        <BarList rows={rows} limit={5} />
      </div>
    </div>
  );
}
